'use client';

import { PurchaseSuccessStepper } from '@hyphengroup/cms-components/cms-blocks/PurchaseSuccessStepper';
import { usePageDataContext } from '$/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import type { CmsProgressStepperComponent } from '$/(cms)/_modules/cmsService/types/components';
import { pickDeliveryNoteHtml } from '$/components/cms/components-user/PolicySummaryCard/deliveryNotes';
import { replacePolicySummaryTokens } from '$/components/cms/components-user/PolicySummaryCard/purchaseTokens';

/**
 * Data-bound wrapper for the Progress Stepper (CMS-1794). The stepper visual is
 * fixed/presentational; the wrapper injects the client-resolved greeting shown
 * below it: it reads the purchase state from the page context slice, selects the
 * greeting matching the purchase status + provider (reusing the delivery-note
 * selection), and interpolates the {First Name} / {Discount} / {Brand} tokens.
 *
 * Until the purchase resolves (`status !== 'ready'`) there is no greeting, so
 * SSR + the first client render agree (SSG/ISR-safe, no hydration mismatch) -
 * the greeting fills in after hydration, like the Policy Summary Card.
 */
export const ProgressStepperWrapper = ({
  completedSteps,
  greetings,
}: CmsProgressStepperComponent): JSX.Element => {
  const {
    pageData: { policySummary },
  } = usePageDataContext();

  let greetingHtml: string | undefined;
  if (policySummary?.status === 'ready' && policySummary.data) {
    const html = pickDeliveryNoteHtml(
      greetings,
      policySummary.isPending ? 'manual_underwriting' : 'success',
      policySummary.data.carrierCode,
    );
    greetingHtml = replacePolicySummaryTokens(html, policySummary);
  }

  return (
    <PurchaseSuccessStepper
      completedSteps={completedSteps}
      greetingHtml={greetingHtml}
    />
  );
};
